"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserContext = void 0;
exports.UserContextProvider = UserContextProvider;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const cancelable_promise_1 = require("cancelable-promise");
const Authentication_1 = __importDefault(require("../shared/Authentication"));
const MyProfile_1 = __importDefault(require("../api/MyProfile"));
const FirebaseCrashlytics_1 = __importDefault(require("../shared/FirebaseCrashlytics"));
const Helper_1 = require("../Helper");
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const SessionContext_1 = require("./SessionContext");
const DeepLinkContext_1 = require("./DeepLinkContext");
const NetworkContext_1 = require("./NetworkContext");
exports.UserContext = react_1.default.createContext(null);
function UserContextProvider(props) {
    const { deepLink, markReady } = react_1.default.useContext(DeepLinkContext_1.DeepLinkContext);
    const sessionContext = react_1.default.useContext(SessionContext_1.SessionContext);
    const employeePromise = react_1.default.useRef(null);
    const [employee, setEmployee] = react_1.default.useState(null);
    const [employeeStatus, setEmployeeStatus] = react_1.default.useState(null);
    const clientPromise = react_1.default.useRef(null);
    const [client, setClient] = react_1.default.useState(null);
    const [clientStatus, setClientStatus] = react_1.default.useState(null);
    const [clientList, setClientList] = react_1.default.useState(null);
    const [clientSelectedId, setClientSelectedId] = react_1.default.useState(null);
    const [primaryClientId, setPrimaryClientId] = react_1.default.useState(null);
    const handleClientScoring = async () => {
        if (!employee || !sessionContext.userId || sessionContext.type !== SessionContext_1.SessionType.Employee)
            return;
        const clientsObj = sessionContext.userToken?.claims?.clients ?? {};
        const clientIds = Object.keys(clientsObj);
        if (!clientIds.length) {
            setClientList(null);
            setClientSelectedId(null);
            setClientStatus('error');
            return;
        }
        const scored = clientIds
            .map((id) => {
            const roleStr = clientsObj[id];
            let score = 0;
            if (roleStr?.includes("A"))
                score += 8;
            if (roleStr?.includes("P"))
                score += 4;
            if (roleStr?.includes("E"))
                score += 3;
            if (roleStr?.includes("C"))
                score += 2;
            if (roleStr?.includes("F"))
                score += 1;
            return { id, role: roleStr, score, onboarding: false };
        })
            .sort((a, b) => b.score - a.score);
        setClientList(scored);
        let localSelected = null;
        if (Helper_1.Helper.isPlatformWeb) {
            localSelected = await async_storage_1.default.getItem('selectedClientId');
        }
        const fetchedPrimaryId = employee?.primaryClientId ?? null;
        const validPrimary = fetchedPrimaryId && clientIds.includes(fetchedPrimaryId) ? fetchedPrimaryId : null;
        setPrimaryClientId(validPrimary);
        const hasLocalSelected = !!localSelected && clientIds.includes(localSelected);
        const chosenId = hasLocalSelected
            ? localSelected
            : (validPrimary ?? scored[0].id);
        setClientSelectedId(chosenId);
        if (Helper_1.Helper.isPlatformWeb) {
            await async_storage_1.default.setItem('selectedClientId', chosenId);
        }
    };
    react_1.default.useEffect(() => {
        setEmployee(null);
        setEmployeeStatus(null);
        if (sessionContext.userId &&
            sessionContext.type == SessionContext_1.SessionType.Employee) {
            if (sessionContext.getSessionTimeout()) {
                Authentication_1.default.SignOut();
                return;
            }
            setEmployeeStatus('loading');
            employeePromise.current = (0, cancelable_promise_1.cancelable)(Authentication_1.default.GetToken().then(t => MyProfile_1.default.getIntroEmployee({ token: t }))).then(data => {
                FirebaseCrashlytics_1.default.setAttribute('employeeId', data.employeeId);
                setEmployee(data);
                setEmployeeStatus('done');
                NetworkContext_1.NetworkErrorSetter.trigger(false);
            }).catch((e) => {
                setEmployee(null);
                if (e == 410) {
                    setEmployeeStatus('update');
                }
                else {
                    setEmployeeStatus('error');
                    if (e?.message?.toLowerCase()?.includes('network request failed') ||
                        e?.message?.toLowerCase()?.includes('failed to fetch')) {
                        NetworkContext_1.NetworkErrorSetter.trigger(true);
                    }
                }
            });
        }
        return () => employeePromise.current?.cancel();
    }, [sessionContext.userId]);
    react_1.default.useEffect(() => {
        setClient(null);
        setClientStatus(null);
        setClientList(null);
        setClientSelectedId(null);
        setPrimaryClientId(null);
        if (sessionContext.userId &&
            sessionContext.type == SessionContext_1.SessionType.Employee) {
            handleClientScoring();
        }
    }, [sessionContext.userId, employee]);
    react_1.default.useEffect(() => {
        if (clientSelectedId) {
            if (clientSelectedId != client?.id) {
                setClientStatus('loading');
                clientPromise.current = (0, cancelable_promise_1.cancelable)(Authentication_1.default.GetToken().then(t => MyProfile_1.default.getIntroClient({
                    token: t,
                    client: clientSelectedId
                }))).then(data => {
                    FirebaseCrashlytics_1.default.setAttribute('clientId', data.clientId);
                    setClient({
                        id: data.clientId,
                        name: data.name,
                        verifyDetails: data.verifyDetails,
                        features: data.features
                    });
                    setClientStatus('done');
                }).catch((e) => {
                    setClient(null);
                    setClientSelectedId(null);
                    setClientStatus('error');
                    pendingNavigationRef.current = null;
                });
            }
        }
        else {
            setClient(null);
        }
        return () => clientPromise.current?.cancel();
    }, [clientSelectedId]);
    const pendingNavigationRef = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        if (clientStatus === 'done' && pendingNavigationRef.current && client?.id === clientSelectedId) {
            const target = pendingNavigationRef.current;
            pendingNavigationRef.current = null;
            setTimeout(() => {
                sessionContext.navigate('Content', {
                    screen: target.tab ?? 'CompanyTab',
                    params: {
                        screen: target.screen,
                        params: target.params,
                    },
                });
            }, 100);
        }
    }, [clientStatus, client?.id, clientSelectedId]);
    react_1.default.useEffect(() => {
        if (client?.verifyDetails &&
            employee?.clients?.[clientSelectedId]?.role.includes('A')) {
            sessionContext.navigate('CompanyDetails');
        }
    }, [client]);
    react_1.default.useEffect(() => {
        if (deepLink.ready)
            return; // to not navigate away from an already loaded deep link
        if (!deepLink.clientOverride)
            return;
        if (!employee)
            return;
        const clientsObj = sessionContext.userToken?.claims?.clients ?? {};
        const clientIds = Object.keys(clientsObj);
        if (!clientIds.includes(deepLink.clientOverride))
            return;
        if (clientSelectedId !== deepLink.clientOverride) {
            setClientSelectedId(deepLink.clientOverride);
            if (Helper_1.Helper.isPlatformWeb) {
                async_storage_1.default.setItem('selectedClientId', deepLink.clientOverride);
            }
        }
    }, [deepLink.ready, deepLink.clientOverride, employee]);
    react_1.default.useEffect(() => {
        if (!deepLink.url)
            return;
        const isReady = employeeStatus === 'done' &&
            clientStatus === 'done' &&
            !!client?.id;
        const clientMatch = deepLink.clientOverride == null ||
            deepLink.clientOverride === client?.id;
        if (isReady && clientMatch) {
            markReady();
        }
    }, [deepLink.url, employeeStatus, clientStatus, client?.id]);
    return ((0, jsx_runtime_1.jsx)(exports.UserContext.Provider, { value: {
            user: sessionContext.userToken, // Data for the logged in user (async).
            appUpdateRequired: (employeeStatus == 'update'),
            employee, // Data for the employee (async).
            employeeStatus, // Status of loading employee data.
            employeeDone: (employeeStatus == 'done'),
            employeeError: (employeeStatus == 'error'),
            employeeLoading: (employeeStatus == 'loading'),
            client, // Data for the selected client (async).
            clientStatus, // Status of loading client data.
            clientDone: (clientStatus == 'done'),
            clientError: (clientStatus == 'error'),
            clientLoading: (clientStatus == 'loading'),
            clientList, // List of clients from user claims.
            // Whether the employee is onboarding for the selected client.
            isOnboarding: (employee?.clients?.[clientSelectedId]?.onboarding ?? false),
            // Only use for visual changes not security checks.
            isInternal: (!!sessionContext.userToken?.claims?.internal ||
                (sessionContext.userToken?.claims?.email.includes('@nextep.com') &&
                    !sessionContext.userToken?.claims?.email?.includes('+'))),
            isApprover: (employee?.clients?.[clientSelectedId]?.role.includes('P') ?? false),
            isAdmin: (employee?.clients?.[clientSelectedId]?.role.includes('A') ?? false),
            isContractor: (employee?.clients?.[clientSelectedId]?.role.includes('C') ?? false),
            isFormer: (employee?.clients?.[clientSelectedId]?.role.includes('F') ?? false),
            permissions: employee?.clients?.[clientSelectedId]?.permissions,
            adminExperience: employee?.clients?.[clientSelectedId]?.adminExperience,
            // Only use for visual changes, not security checks.
            isImpersonated: (!!sessionContext.userToken?.claims?.impersonator),
            isDemo: (sessionContext.userToken?.claims?.demo === "True"),
            isSecureLogin: ((sessionContext.userToken?.signInSecondFactor ?? sessionContext.userToken?.claims?.firebase?.sign_in_second_factor) || // MFA with SMS or TOTP
                sessionContext.userToken?.claims?.trustedDevice || // Trusted Device
                ['face', 'fpt'].includes(sessionContext.userToken?.claims?.app_amr) // Biometric
            ),
            primaryClientId,
            // Used to conditionally refresh employee data during onboarding.
            refreshEmployee: () => {
                employeePromise.current?.cancel();
                employeePromise.current = (0, cancelable_promise_1.cancelable)(Authentication_1.default.GetToken().then(t => MyProfile_1.default.getIntroEmployee({ token: t }))).then(data => {
                    return { result: data, update: () => setEmployee(data) };
                });
                return employeePromise.current;
            },
            // Get user token and selected client.
            getToken: () => Authentication_1.default.GetToken().then((token) => ({ token, client: client?.id })),
            // Change the selected client.
            selectClient: async (id, targetNavigation = null) => {
                if (id === client?.id) {
                    if (targetNavigation) {
                        sessionContext.navigate('Content', {
                            screen: targetNavigation.tab ?? 'CompanyTab',
                            params: {
                                screen: targetNavigation.screen,
                                params: targetNavigation.params,
                            },
                        });
                    }
                    return;
                }
                if (targetNavigation) {
                    pendingNavigationRef.current = targetNavigation;
                }
                setClientSelectedId(id);
                if (Helper_1.Helper.isPlatformWeb) {
                    await async_storage_1.default.setItem('selectedClientId', id);
                }
            },
            updatePrimaryClientId: (clientId) => {
                setPrimaryClientId(clientId);
            },
        }, children: props.children }));
}
;
